import { useMemo, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import NsContent from 'components/common/ns-content/NsContent';
import NsColumn from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { Feature } from 'components/common/feature/Feature';
import { NsxAddressSelector } from 'components/common/nsx-address-selector/NsxAddressSelector';
import { NsStopgap } from 'components/common/ns-stopgap/NsStopgap';
import { NsCard } from 'components/common/ns-card/NsCard';
import { useNavigation } from 'hooks/useNavigation';
import { ToggleJourney } from 'components/app/routes/ToggleJourney';
import { selectors as meterTypeSelectors } from 'state/meter-type/meter-type-slice';
import { useQueryParam } from 'hooks/useQueryParams';
import { HighDemandAlert } from 'components/common/error/components/HighDemandAlert';

const { selectIsLoading: selectIsLoadingMeterType } = meterTypeSelectors;

export const testId = 'directSalesTestId';

export const DirectSales = () => {
  const { allowJourneyDefault } = useFlags();
  const defaultPostcode = useQueryParam('postcode');
  const isLoadingMeterType = useSelector(
    selectIsLoadingMeterType,
    shallowEqual
  );
  const { next, eventTypes } = useNavigation();

  const handleGoNext = useCallback(() => {
    next({ eventType: eventTypes.NEXT });
  }, [next, eventTypes.NEXT]);

  return useMemo(() => {
    if (isLoadingMeterType) {
      return <NsStopgap />;
    }

    return (
      <Feature
        feature={allowJourneyDefault}
        inactiveComponent={<ToggleJourney off />}
      >
        <>
          <link
            rel="canonical"
            href="https://www.britishgas.co.uk/new-energy/sales"
          />
          <NsPanel testId={testId}>
            <NsContent>
              <HighDemandAlert />
              <NsTitle title="Get an energy quote" />

              <p>
                Join the UK&apos;s leading energy supplier. You&apos;ll find our
                cheapest gas and electricity tariffs right here.
              </p>
            </NsContent>

            <NsxAddressSelector
              handleGoNext={handleGoNext}
              defaultPostcode={defaultPostcode}
            />
          </NsPanel>
          <NsPanel>
            <NsContent>
              <h2>Why get a gas & electricity quote from us?</h2>
            </NsContent>
            <NsColumn basketball-court="2" hockey-pitch="3">
              <NsCard
                type="section"
                heading="Service you can rely on"
                decoration="rosette-tick-green"
              >
                <p>
                  Just ask our customers – 148,000+ rated us 5 stars on
                  Trustpilot
                </p>
              </NsCard>

              <NsCard
                type="section"
                heading="Save money with PeakSave"
                decoration="savings"
              >
                <p>
                  By using their smart meters, more than 750,000 customers save
                  money with PeakSave every month
                </p>
              </NsCard>
            </NsColumn>
          </NsPanel>
        </>
      </Feature>
    );
  }, [allowJourneyDefault, isLoadingMeterType, defaultPostcode, handleGoNext]);
};

export default DirectSales;
